import React, { useState } from 'react';
import { FiFileText, FiUsers, FiLayers } from 'react-icons/fi';

const QNavGovernance: React.FC = () => {
  // Organization structure mock data
  const [orgStructure] = useState([
    {
      id: 'org-1',
      name: 'Q Developer Center of Excellence',
      lead: 'Maria Rodriguez',
      members: 8,
      description: 'Responsible for establishing Q Developer governance policies and best practices.',
    },
    {
      id: 'org-2',
      name: 'Developer Experience Team',
      lead: 'David Chen',
      members: 5,
      description: 'Ensures optimal developer experience and adoption across teams.',
    },
    {
      id: 'org-3',
      name: 'Q Developer Champions',
      lead: 'James Wilson',
      members: 12,
      description: 'Network of champions promoting Q Developer adoption across the organization.',
    },
    {
      id: 'org-4',
      name: 'Enterprise Architecture',
      lead: 'Sophia Patel',
      members: 6,
      description: 'Ensures Q Developer aligns with enterprise architecture standards.',
    },
  ]);

  // Governance charter mock data
  const [charter] = useState([
    {
      id: 'charter-1',
      title: 'Q Developer Adoption Framework',
      description: 'Guidelines for Q Developer adoption across the organization.',
      status: 'Approved',
      lastUpdated: '2025-02-15',
    },
    {
      id: 'charter-2',
      title: 'Usage Policies',
      description: 'Policies governing appropriate use of Q Developer features.',
      status: 'Under Review',
      lastUpdated: '2025-03-20',
    },
    {
      id: 'charter-3',
      title: 'KPI Tracking',
      description: 'Framework for tracking key performance indicators for Q Developer adoption.',
      status: 'Approved',
      lastUpdated: '2025-01-10',
    },
    {
      id: 'charter-4',
      title: 'Champion Network Guidelines',
      description: 'Structure and responsibilities for the Q Developer champion network.',
      status: 'Draft',
      lastUpdated: '2025-04-05',
    },
  ]);

  // Resources mock data (for future use)
  /*
  const [resources, setResources] = useState([
    {
      id: 'resource-1',
      title: 'Q Developer Best Practices',
      type: 'Documentation',
      link: '#',
    },
    {
      id: 'resource-2',
      title: 'Enterprise Adoption Playbook',
      type: 'Documentation',
      link: '#',
    },
    {
      id: 'resource-3',
      title: 'Governance Templates',
      type: 'Templates',
      link: '#',
    },
    {
      id: 'resource-4',
      title: 'Change Management Workshop',
      type: 'Workshop',
      link: '#',
    },
  ]);
  */

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Approved':
        return (
          <span className="px-2 py-1 bg-success-100 text-success-700 text-xs font-medium rounded-full">Approved</span>
        );
      case 'Under Review':
        return (
          <span className="px-2 py-1 bg-warning-100 text-warning-700 text-xs font-medium rounded-full">
            Under Review
          </span>
        );
      case 'Draft':
        return <span className="px-2 py-1 bg-info-100 text-info-700 text-xs font-medium rounded-full">Draft</span>;
      default:
        return (
          <span className="px-2 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full">
            Unknown
          </span>
        );
    }
  };

  return (
    <div className="h-full flex flex-col space-y-6">
      <div className="bg-brand-gradient text-white p-6 rounded-qnav shadow-brand-card">
        <h1 className="text-2xl font-display font-bold mb-2">Intelligent Governance</h1>
        <p className="text-secondary-200">Establish and maintain governance for your Q Developer enterprise adoption</p>
      </div>

      {/* Organization Structure */}
      <div className="bg-surface rounded-qnav shadow-brand-card border border-secondary-200 overflow-hidden">
        <div className="p-4 border-b border-secondary-200 bg-secondary-50 flex items-center">
          <FiUsers className="text-primary-600 mr-2" />
          <h2 className="text-xl font-semibold text-secondary-800">Organization Structure</h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {orgStructure.map((org) => (
              <div key={org.id} className="bg-surface-blue p-4 rounded-qnav border border-primary-100">
                <h3 className="font-semibold text-secondary-800">{org.name}</h3>
                <div className="mt-2 text-sm text-secondary-600">{org.description}</div>
                <div className="mt-3 flex justify-between items-center">
                  <div className="text-sm text-secondary-700">
                    <span className="font-medium">Lead:</span> {org.lead}
                  </div>
                  <div className="text-sm bg-primary-100 text-primary-700 px-2 py-1 rounded-full">
                    {org.members} members
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Program Charter */}
      <div className="bg-surface rounded-qnav shadow-brand-card border border-secondary-200 overflow-hidden">
        <div className="p-4 border-b border-secondary-200 bg-secondary-50 flex items-center">
          <FiFileText className="text-primary-600 mr-2" />
          <h2 className="text-xl font-semibold text-secondary-800">Program Charter</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary-100">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-secondary-700">Title</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-secondary-700">Description</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-secondary-700">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-secondary-700">Last Updated</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-secondary-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-200">
              {charter.map((item) => (
                <tr key={item.id} className="hover:bg-secondary-50">
                  <td className="px-4 py-4 font-medium text-secondary-800">{item.title}</td>
                  <td className="px-4 py-4 text-secondary-600">{item.description}</td>
                  <td className="px-4 py-4">{getStatusBadge(item.status)}</td>
                  <td className="px-4 py-4 text-secondary-700">{item.lastUpdated}</td>
                  <td className="px-4 py-4">
                    <button className="text-primary-600 hover:text-primary-800 font-medium">View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Organizational Analysis */}
      <div className="bg-brand-blue rounded-qnav shadow-brand-card p-6 text-white">
        <div className="flex items-center mb-4">
          <FiLayers className="mr-2" />
          <h2 className="text-xl font-semibold">Organizational Analysis</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-secondary-800 p-4 rounded-qnav">
            <h3 className="font-medium mb-1">Change Management Capability</h3>
            <div className="flex items-center mt-2">
              <div className="w-full bg-secondary-700 rounded-full h-2.5">
                <div className="bg-success-500 h-2.5 rounded-full" style={{ width: '75%' }}></div>
              </div>
              <span className="ml-2 text-sm text-secondary-300">75%</span>
            </div>
            <p className="mt-2 text-sm text-secondary-400">Strong change management processes in place</p>
          </div>
          <div className="bg-secondary-800 p-4 rounded-qnav">
            <h3 className="font-medium mb-1">Transformation Complexity</h3>
            <div className="flex items-center mt-2">
              <div className="w-full bg-secondary-700 rounded-full h-2.5">
                <div className="bg-warning-500 h-2.5 rounded-full" style={{ width: '60%' }}></div>
              </div>
              <span className="ml-2 text-sm text-secondary-300">60%</span>
            </div>
            <p className="mt-2 text-sm text-secondary-400">Moderate complexity due to legacy systems</p>
          </div>
          <div className="bg-secondary-800 p-4 rounded-qnav">
            <h3 className="font-medium mb-1">Developer Readiness</h3>
            <div className="flex items-center mt-2">
              <div className="w-full bg-secondary-700 rounded-full h-2.5">
                <div className="bg-error-500 h-2.5 rounded-full" style={{ width: '45%' }}></div>
              </div>
              <span className="ml-2 text-sm text-secondary-300">45%</span>
            </div>
            <p className="mt-2 text-sm text-secondary-400">Additional training needed</p>
          </div>
          <div className="bg-secondary-800 p-4 rounded-qnav">
            <h3 className="font-medium mb-1">Executive Support</h3>
            <div className="flex items-center mt-2">
              <div className="w-full bg-secondary-700 rounded-full h-2.5">
                <div className="bg-success-500 h-2.5 rounded-full" style={{ width: '90%' }}></div>
              </div>
              <span className="ml-2 text-sm text-secondary-300">90%</span>
            </div>
            <p className="mt-2 text-sm text-secondary-400">Strong executive sponsorship</p>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      <div className="bg-surface rounded-qnav shadow-brand-card p-6 border border-secondary-200">
        <h2 className="text-xl font-semibold text-secondary-800 mb-4">Recommendations</h2>
        <div className="space-y-4">
          <div className="p-4 bg-surface-blue rounded-qnav border border-primary-100">
            <h3 className="font-medium text-secondary-800 mb-2">Resource Allocation</h3>
            <p className="text-secondary-600 mb-3">
              Based on your organizational analysis, we recommend allocating 3 additional full-time resources to the
              Developer Experience team to accelerate adoption.
            </p>
            <button className="text-primary-600 hover:text-primary-700 font-medium">View Details</button>
          </div>

          <div className="p-4 bg-surface-blue rounded-qnav border border-primary-100">
            <h3 className="font-medium text-secondary-800 mb-2">Timeline Planning</h3>
            <p className="text-secondary-600 mb-3">
              Your organization is on track for a 6-month rollout across all development teams. Consider a phased
              approach starting with the frontend teams.
            </p>
            <button className="text-primary-600 hover:text-primary-700 font-medium">View Timeline</button>
          </div>

          <div className="p-4 bg-surface-accent rounded-qnav border border-accent-100">
            <h3 className="font-medium text-secondary-800 mb-2">Next Steps</h3>
            <p className="text-secondary-600 mb-3">
              Schedule a developer readiness assessment workshop to identify specific training needs and develop a
              targeted training plan.
            </p>
            <button className="text-accent-600 hover:text-accent-700 font-medium">Schedule Workshop</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QNavGovernance;
